import React, { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Button,
  Card,
  DOCKED_CARD_WIDTHS,
  DockedCard,
  Icon,
  Skeleton,
  Slider,
  Switch,
  Tag,
  type IconName,
} from '@repo/design-system';
import { fontMono, radius, semantic, space, typeRole } from '@repo/design-tokens';
import { getTokenBounded, getTwinById } from '@repo/front-end-services';
import { useFloorplanStore, useUserStore } from '@repo/front-end-state';
import { findLatestDesign, type OnboardingRoute } from '@repo/front-end-utils';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { AdminTwin, PositionSource } from '@repo/frontend-types';
import { readCameraStart, visuallyHidden } from '@repo/ui';

import Config from '../../common/Config';
import {
  OPENING_VIEW_PICTURE_HEIGHT_PX,
  OPENING_VIEW_PICTURE_WIDTH_PX,
  PlanOpeningViewRendererContext,
  PlanPlacementRendererContext,
  placementOutline,
  type PlacementChange,
  type PlacementShown,
} from '../../components/3D/planView/planPlacementRenderer';
import {
  describeView,
  openingViewFromCamera,
  withinRange,
  type MapCamera,
} from '../../components/3D/planView/openingView';
import { PLAN_DRAW_TIMEOUT_MS } from '../../components/3D/planView/planDrawState';
import PositionControls from './PositionControls';
import { GLASS_EDGE, GLASS_EDGE_PX, GLASS_START, PANEL_TOP, PANEL_TOP_PX } from './glassLayout';
import { PlanCard, usePlanCardLayer } from './planCard';
import { POSITION_ROWS, positionSentence, type PositionRowKey } from './positionOnMapRows';
import { useAddressGeocode } from './useAddressGeocode';
import { useOpeningViewPicture } from './useOpeningViewPicture';
import { usePositionEditor } from './usePositionEditor';
import type { TwinDetailsSave } from './useTwinDetailsSave';
import { positionSourceTag } from './twinValueSources';
import {
  formFromTwin,
  looksLikePlaceholderOrigin,
  orientationError,
  positionPatch,
  TWIN_REQUEST_TIMEOUT_MS,
  type TwinDetailsPatch,
} from './twinDetailsForm';

/**
 * **Position on map** — where the twin sits and which way it faces
 * (`retire-edit-mode-position-write-path`; first built as `twin-details-page-position-placeholder`).
 *
 * A TL_ADMIN types the twin's latitude, longitude and orientation here, for placed and unplaced
 * twins alike, or takes the address's position (*Find*, or *Reset to address position* behind a
 * confirm on a twin already placed). **Every change saves straight to the twin** through the page's
 * Twin details queue — one write path, not a second one — and the title bar shows that queue's tag.
 * The controls and their handlers are the ones Twin details uses (`PositionControls`,
 * `usePositionEditor`), extended rather than copied.
 *
 * THE OPENING VIEW (`retire-edit-mode-opening-view`): a turn or tilt of the map (a right-drag, two
 * fingers, or Shift with the arrow keys on the map element) releases it from top-down into the free
 * camera, where the map is (`onboarding-persistent-scene-position-twin-modes`, R3), and *Use this
 * view* saves what it shows, within 500 m of the twin, as the view the twin viewer opens on, through
 * the same `commit`. *Switch back to plan view* is offered only while the map is free (R4).
 * The Opening view card shows a picture of the saved view, drawn afresh on each visit by a hidden map
 * that draws only it, and never stored.
 *
 * THE MAP (`retire-edit-mode-place-on-map`): the onboarding plan drawn from above at the twin's origin
 * and orientation, filling the window under the glass on the page's backdrop
 * (`onboarding-persistent-scene-position-twin`, R2.1). The step portals its map controls and the
 * canvas's overlay over it, and holds the fields, the opening view and the satellite check in one
 * movable panel at the top right (R3). There are no modes
 * (`onboarding-persistent-scene-position-twin-modes`): the person drags the plan to move the twin and
 * the floor plan editor's turn ring, sized to the plan, to turn it about the plan's centre, at any
 * tilt, and drags anywhere else to move the camera. Two keyboard controls nudge and turn it. Each
 * drag, turn or key run
 * saves once through the same `commit` as the fields, and the fields follow every move. The map is the
 * renderer chunk's (`PlanPlacementRendererContext`), so the step mounts inside the page's
 * `PlanRendererProviders`; with no plan to drag the step says why, and typing still works.
 *
 * IT BLOCKS NOTHING. The page never reads it into `canLaunch`; its tick is the grey "seen" tick,
 * owned by the page.
 *
 * IT READS AFRESH on every arrival rather than taking the page's twin read, ONCE THE QUEUE HAS
 * SETTLED (R7): a change made on Twin details just before arriving is never shown as its old value,
 * and another tab or an API caller may have changed any of the three. A failed read says so and shows
 * no fields (R10) — never *Not set*, which would claim the twin has no position when the truth is
 * that we could not find out, and never a field typed over a record the step has not read.
 */

interface Props {
  className?: string;
  /** The twin being onboarded — `?twinBid=`. */
  twinBid: string | undefined;
  /** The twin's route, for the sentence: a live twin's Launch keeps its origin. */
  route: OnboardingRoute | null;
  /** Auth0's `getAccessTokenSilently`; bounded here. */
  getAccessToken: () => Promise<string>;
  /** The page-level Twin details save queue (R5). */
  save: TwinDetailsSave;
  /**
   * The page's read of the twin's plan (`planState`): `failed` says the plan could not be read and
   * `loading` that it is still being read. Otherwise the store's layout decides (R11): a twin with
   * no saved plan reads `none`, and keeps reading `none` after this session draws and saves its
   * first floors. Absent: no map is drawn.
   */
  planRead?: 'loading' | 'none' | 'loaded' | 'failed' | 'unread';
  /**
   * The floorplan store's key for the plan `planRead` describes. The wizard loads its plan under its
   * session id, never the twin's BID, so the step draws the store's layout only while it carries
   * this key.
   */
  planScope?: string;
}

/** R11: why there is nothing to drag. */
export const PLAN_EMPTY_NOTE =
  'This twin’s floor plan is empty, so there is nothing to drag. Type the position and orientation below.';
export const PLAN_FAILED_NOTE =
  'The floor plan could not be read, so there is nothing to drag. Type the position and orientation below.';
/** `onboarding-persistent-scene-position-twin-modes`, R6.1: the help line, at all times. */
export const PLACEMENT_HELP =
  'Drag the plan to move it, and the ring to turn it (Shift snaps to 15°). Drag the map to pan; right-drag to turn and tilt; scroll to zoom.';
/** R6.1: the keyboard's line, visually hidden, for the map element. */
export const KEYBOARD_HELP =
  'On the map, arrow keys pan; with Shift they turn and tilt; + and − zoom. Move the twin and Turn the twin take arrow keys: 0.1 m or 0.1°, with Shift 1 m or 1°.';
/** R4.3's announcements. */
export const LOOK_AROUND_ON = 'Looking around. Use this view saves what the map shows.';
export const LOOK_AROUND_OFF = 'Back to the top-down map.';
export const TOO_FAR_SHORT = 'Too far from the twin to use.';
export const TOO_FAR = `${TOO_FAR_SHORT} Move the view back within 500 m.`;
export const PICTURE_FAILED_ANNOUNCEMENT = 'The picture of the opening view could not be drawn.';
/** R6.2: what *Use this view* is described by at the plan view. */
export const TILT_FIRST = 'Turn or tilt the map to the view you want first.';
/**
 * The one camera button (`onboarding-persistent-scene-position-twin-modes`, R4.1), offered only while
 * the map is out of the plan view.
 */
export const SWITCH_BACK_LABEL = 'Switch back to plan view';
/** The panel's title, and the name in its grip's *Move …* (R3.2). */
export const PANEL_TITLE = 'Position and orientation';
export const PLACE_FIRST = 'Place the twin first. The opening view is set around its position.';
export const NO_VIEW = 'No opening view saved. The twin viewer opens on a default view.';
export const UNREADABLE_VIEW =
  'The saved opening view cannot be read. The twin viewer opens on a default view.';
export const PICTURE_FAILED = 'The picture could not be drawn.';
/** No machine's footprint: each counts by its centre point. */
const NO_FOOTPRINTS = () => undefined;

/** The fields this step saves: a clash on any of them drops its commit (R3.3). */
const POSITION_FIELDS = ['latitude', 'longitude', 'position_source', 'orientation'];

/** R10: where the opacity starts on every arrival. */
const SATELLITE_START_OPACITY = 60;
export const SATELLITE_FAILED = 'Satellite imagery could not be loaded';

type Read =
  | { state: 'loading' }
  /** `bid`: the twin the read was for. Fields show only while it is still the twin on screen. */
  /** `startedAt`: the step's clock when this read began (`nextTick`). */
  | { state: 'ready'; twin: AdminTwin; bid: string; startedAt: number }
  | { state: 'failed'; retrying: boolean };

const ROW_ICON: Record<PositionRowKey, IconName> = {
  position: 'location-dot',
  orientation: 'compass',
  view: 'camera',
};

/**
 * What the step last handed to the queue, for the announcement once it is saved (criterion 13): what
 * it carried, in the card's order (`retire-edit-mode-opening-view`, R8, adds the opening view).
 */
const savedAnnouncement = (patch: TwinDetailsPatch): string => {
  const parts = [
    'latitude' in patch && 'position',
    patch.orientation !== undefined && 'orientation',
    patch.camera_start_args !== undefined && 'opening view',
  ].filter((part): part is string => !!part);
  if (parts.length === 0) return 'Position saved';
  const said =
    parts.length === 1
      ? parts[0]
      : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `${said.charAt(0).toUpperCase()}${said.slice(1)} saved`;
};

/** The twin's position as the step last saved it: the record, then every patch handed to the queue. */
interface Baseline {
  latitude: string;
  longitude: string;
  positionSource: PositionSource | null;
  orientation: string;
  /** The stored opening view, as stored (`retire-edit-mode-opening-view`): read by `readCameraStart`. */
  view: unknown;
}

const baselineOf = (twin: AdminTwin): Baseline => {
  const form = formFromTwin(twin);
  return {
    latitude: form.latitude,
    longitude: form.longitude,
    positionSource: twin.position_source ?? null,
    orientation: twin.orientation == null ? '' : String(twin.orientation),
    view: twin.camera_start_args ?? null,
  };
};

/** The map's camera, as the view would be saved from it. */
const cameraOf = (map: MapboxMap): MapCamera => {
  const c = map.getCenter();
  return {
    lat: c.lat,
    lng: c.lng,
    zoom: map.getZoom(),
    bearing: map.getBearing(),
    pitch: map.getPitch(),
  };
};

/**
 * A clock for ordering a read against the saves made while it was out: a save stamped after a read
 * began is newer than that read's answer. A counter, not `Date.now()`, so two events never tie.
 */
let clock = 0;
const nextTick = () => (clock += 1);

const samePair = (a: Baseline, b: { latitude: string; longitude: string }) =>
  a.latitude.trim() === b.latitude.trim() && a.longitude.trim() === b.longitude.trim();

interface FieldsProps {
  twin: AdminTwin;
  /** When the read that gave `twin` began. */
  readStartedAt: number;
  twinBid: string;
  save: TwinDetailsSave;
  onAnnounce: (message: string) => void;
  planRead: Props['planRead'];
  planScope: Props['planScope'];
  /** The values card, for the focus after *Try again*. */
  valuesRef: React.Ref<HTMLDivElement>;
  /**
   * Where the map's pieces go (`onboarding-persistent-scene-position-twin`, R4): the map controls and
   * the map's overlay, over the whole window and before the panel in the tab order. The panel holds
   * the rest.
   */
  mapHost: HTMLElement | null;
}

/**
 * The ready step: the map (when the renderer provides one) and the fields, mounted only once the twin
 * is read, so they start from the record.
 */
const PositionFields: React.FC<FieldsProps> = ({
  twin,
  readStartedAt,
  twinBid,
  save,
  onAnnounce,
  planRead,
  planScope,
  valuesRef,
  mapHost,
}) => {
  const { authMetadata } = useUserStore();
  const [baseline, setBaseline] = useState<Baseline>(() => baselineOf(twin));
  // The address Find and Reset search with is the record's: this step never edits it.
  const address = useMemo(() => formFromTwin(twin), [twin]);
  const geocode = useAddressGeocode(address, authMetadata?.tokens?.mapboxToken);

  /** The keys last handed to the queue, announced when the queue reports saved. */
  const committedRef = useRef<TwinDetailsPatch | null>(null);
  /** The fields of this twin's last clash, read when the tag turns to it. */
  const clashFieldsRef = useRef<string[]>([]);
  clashFieldsRef.current = save.clash?.bid === twinBid ? save.clash.fields : [];
  /** When this step last handed a patch to the queue (`nextTick`). */
  const lastCommitAtRef = useRef(0);
  const queue = save.save;
  const commit = useCallback(
    (patch: TwinDetailsPatch) => {
      queue(patch, twinBid);
      lastCommitAtRef.current = nextTick();
      committedRef.current = { ...(committedRef.current ?? {}), ...patch };
      setBaseline(prev => ({
        latitude: 'latitude' in patch ? (patch.latitude ?? '') : prev.latitude,
        longitude: 'longitude' in patch ? (patch.longitude ?? '') : prev.longitude,
        positionSource: 'latitude' in patch ? (patch.position_source ?? null) : prev.positionSource,
        orientation: patch.orientation ?? prev.orientation,
        view: patch.camera_start_args ?? prev.view,
      }));
    },
    [queue, twinBid],
  );

  const editor = usePositionEditor({
    twinBid,
    saved: baseline,
    address,
    geocode,
    commit,
    withOrientation: true,
  });

  /*
   * The map's live gesture or key run (R7): the fields show its values while it runs, then the values
   * it saved. `null` when nothing is live.
   */
  const [live, setLive] = useState<PlacementChange | null>(null);
  const liveRef = useRef(live);
  liveRef.current = live;
  // A turn moves the origin too (`onboarding-persistent-scene-position-twin-modes`, R2.8).
  const shown = useMemo<PlacementShown>(
    () => ({
      latitude: live ? live.latitude : baseline.latitude,
      longitude: live ? live.longitude : baseline.longitude,
      orientation: live?.kind === 'turn' ? live.orientation : baseline.orientation,
    }),
    [live, baseline],
  );
  const baselineNowRef = useRef(baseline);
  baselineNowRef.current = baseline;
  const reseedTo = editor.reseed;
  const onPreview = useCallback(
    (change: PlacementChange | null) => {
      setLive(change);
      const base = baselineNowRef.current;
      reseedTo({
        latitude: change ? change.latitude : base.latitude,
        longitude: change ? change.longitude : base.longitude,
        orientation: change?.kind === 'turn' ? change.orientation : base.orientation,
      });
    },
    [reseedTo],
  );
  /*
   * A turn is about the plan's centre, so it saves the orientation and the origin it moved as ONE
   * patch (`onboarding-persistent-scene-position-twin-modes`, R2.8): both land or neither does.
   */
  const onPlacementCommit = useCallback(
    (change: PlacementChange) => {
      const base = baselineNowRef.current;
      const pair = positionPatch(
        { latitude: change.latitude, longitude: change.longitude },
        'entered',
      );
      let patch: TwinDetailsPatch | null;
      if (change.kind === 'move') {
        patch = samePair(base, change) ? null : pair;
      } else {
        // Unchanged only when neither the pair nor the orientation differs: a re-read during the turn
        // may already hold the turned orientation at another pair (Copilot review of #2499).
        const orientation = change.orientation.trim();
        const unchanged = samePair(base, change) && orientation === base.orientation.trim();
        const refused = !pair || orientationError(change.orientation, base.orientation);
        patch = unchanged || refused ? null : { ...pair, orientation };
      }
      setLive(null);
      // Unchanged (a turn back to where it started) or refused: nothing is saved.
      if (!patch) {
        reseedTo(base);
        return;
      }
      commit(patch);
      reseedTo({
        latitude: change.latitude,
        longitude: change.longitude,
        orientation: change.kind === 'turn' ? change.orientation : base.orientation,
      });
    },
    [commit, reseedTo],
  );

  /*
   * A re-read of the same twin (after a held save landed) moves the baseline in place rather than
   * remounting the fields (Copilot review): a value being typed is kept, and only untouched fields
   * take the record's values. An answer older than a save this step made since the read began is
   * dropped, so it never puts back what that save replaced.
   */
  const appliedRef = useRef(twin);
  const baselineRef = useRef(baseline);
  baselineRef.current = baseline;
  const draftRef = useRef({ coords: editor.coords, orientation: editor.orientation });
  draftRef.current = { coords: editor.coords, orientation: editor.orientation };
  const { reseed, onCoordChange, onOrientationChange } = editor;
  useEffect(() => {
    if (appliedRef.current === twin) return;
    appliedRef.current = twin;
    if (lastCommitAtRef.current > readStartedAt) return;
    const next = baselineOf(twin);
    // A gesture is live: its preview stays on screen, and its release saves over the new baseline.
    if (liveRef.current) {
      setBaseline(next);
      return;
    }
    const was = baselineRef.current;
    const draft = draftRef.current;
    const pairTyped = !samePair(was, draft.coords);
    const orientationTyped = was.orientation.trim() !== draft.orientation.trim();
    setBaseline(next);
    if (!pairTyped && !orientationTyped) {
      reseed(next);
      return;
    }
    // Only what is being typed is kept: an untouched half takes the record's value, or leaving
    // the group would send the value from before the save that has just landed.
    if (!pairTyped) {
      onCoordChange('latitude', next.latitude);
      onCoordChange('longitude', next.longitude);
    }
    if (!orientationTyped) onOrientationChange(next.orientation);
  }, [twin, readStartedAt, reseed, onCoordChange, onOrientationChange]);

  // Criterion 13: say what was saved, or that it was not, once the queue reports it.
  const status = save.status;
  const lastStatusRef = useRef(status);
  useEffect(() => {
    const was = lastStatusRef.current;
    lastStatusRef.current = status;
    if (was === status || !committedRef.current) return;
    if (status === 'saved') {
      onAnnounce(savedAnnouncement(committedRef.current));
      committedRef.current = null;
    } else if (status === 'failed') {
      onAnnounce('The twin could not be saved');
    } else if (status === 'blocked') {
      // Someone else changed the position or orientation, so this commit was dropped
      // (`onboarding-multiplayer-twin-record-checks`, R3.3); the tag says so, and a later save must
      // not announce it as saved. A clash on another field (a merged name, say) leaves this commit
      // sent on: it was saved, and is announced as saved (review).
      const fields = clashFieldsRef.current;
      if (POSITION_FIELDS.some(key => fields.includes(key))) {
        committedRef.current = null;
      } else {
        onAnnounce(savedAnnouncement(committedRef.current));
        committedRef.current = null;
      }
    }
  }, [status, onAnnounce]);

  const tag = baseline.latitude.trim()
    ? positionSourceTag(baseline.positionSource, looksLikePlaceholderOrigin(baseline))
    : undefined;

  // ── The map (R1–R13) ──
  const Canvas = useContext(PlanPlacementRendererContext);
  const layout = useFloorplanStore(st => st.layout);
  const storeScope = useFloorplanStore(st => st.twinBID);
  const planLayout = planScope !== undefined && storeScope === planScope ? layout : null;
  const design0 = planLayout?.floors[0] ? findLatestDesign(planLayout.floors[0].designs) : null;
  // Whether the lowest floor draws anything: a machine with no footprint known yet still counts, by
  // its centre point, so no footprints are needed to tell. Once per design: a preview re-renders the
  // step on every pointer move.
  const draws = useMemo(
    () => !!design0 && placementOutline(design0, NO_FOOTPRINTS) !== null,
    [design0],
  );
  // The read only says the plan failed or is still coming; what the store draws decides the rest.
  const draggable = planRead !== 'failed' && planRead !== 'loading' && !!planLayout && draws;
  const note =
    planRead === 'failed'
      ? PLAN_FAILED_NOTE
      : planRead === 'loading' || draggable
        ? null
        : PLAN_EMPTY_NOTE;
  const helpId = useId();
  const keyboardHelpId = useId();
  const [satelliteOn, setSatelliteOn] = useState(false);
  const [opacity, setOpacity] = useState(SATELLITE_START_OPACITY);
  const [satelliteFailed, setSatelliteFailed] = useState(false);
  const satellite = useMemo(() => ({ on: satelliteOn, opacity }), [satelliteOn, opacity]);
  const warnedSatelliteRef = useRef(false);
  const onSatelliteError = useCallback((message: string) => {
    setSatelliteFailed(true);
    if (!warnedSatelliteRef.current) {
      warnedSatelliteRef.current = true;
      console.warn('[onboarding] satellite check could not load', message);
    }
  }, []);

  // ── The opening view (`retire-edit-mode-opening-view`) ──
  /**
   * Whether the map is out of the plan view, as the canvas reads the lock
   * (`onboarding-persistent-scene-position-twin-modes`, R4.1): never on arrival, never stored.
   */
  const [free, setFree] = useState(false);
  /** Bumped by *Switch back to plan view* (R4.2). */
  const [planViewRequest, setPlanViewRequest] = useState(0);
  /** Whether the map has drawn: the camera button is offered only then (R4.1). */
  const [mapDrawn, setMapDrawn] = useState(false);
  const [map, setMap] = useState<MapboxMap | null>(null);
  /** R4.3: the free camera's own polite region, refilled so the same words are said again. */
  const [lookMessage, setLookMessage] = useState('');
  const lookTimerRef = useRef<number | undefined>(undefined);
  const sayLook = useCallback((message: string) => {
    setLookMessage('');
    window.clearTimeout(lookTimerRef.current);
    lookTimerRef.current = window.setTimeout(() => setLookMessage(message), 50);
  }, []);
  useEffect(() => () => window.clearTimeout(lookTimerRef.current), []);

  /** R6: a view is set around a real position: none, or the old placeholder, is placed first. */
  const placedOrigin = useMemo(() => {
    const latitude = Number(baseline.latitude.trim());
    const longitude = Number(baseline.longitude.trim());
    if (!baseline.latitude.trim() || !baseline.longitude.trim()) return null;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    if (looksLikePlaceholderOrigin(baseline)) return null;
    return { latitude, longitude };
  }, [baseline]);
  const storedView = useMemo(() => readCameraStart(baseline.view), [baseline.view]);
  const hasStoredValue = baseline.view != null;

  /** R4 and R5: the view read back after each move ends, while the map is free. */
  const [readout, setReadout] = useState<{ text: string; tooFar: boolean } | null>(null);
  const wasTooFarRef = useRef(false);
  useEffect(() => {
    if (!map || !free || !placedOrigin) {
      setReadout(null);
      wasTooFarRef.current = false;
      return undefined;
    }
    const read = () => {
      let camera: MapCamera;
      try {
        camera = cameraOf(map);
      } catch {
        return;
      }
      const tooFar = !withinRange(camera, placedOrigin);
      setReadout({ text: describeView(camera, placedOrigin), tooFar });
      if (tooFar && !wasTooFarRef.current) sayLook(TOO_FAR_SHORT);
      wasTooFarRef.current = tooFar;
    };
    read();
    map.on('moveend', read);
    return () => {
      map.off('moveend', read);
    };
  }, [map, free, placedOrigin, sayLook]);

  // R4.3: entering the free camera, and coming back from it, are said in the polite region.
  const wasFreeRef = useRef(false);
  useEffect(() => {
    if (free === wasFreeRef.current) return;
    wasFreeRef.current = free;
    sayLook(free ? LOOK_AROUND_ON : LOOK_AROUND_OFF);
  }, [free, sayLook]);

  /** R7: what the map shows, saved once through the queue; nothing while it cannot be used. */
  const useThisView = () => {
    if (!free || !map || !placedOrigin) return;
    let result: ReturnType<typeof openingViewFromCamera>;
    try {
      result = openingViewFromCamera(cameraOf(map), placedOrigin);
    } catch {
      return;
    }
    if (!result.ok) return;
    commit({ camera_start_args: result.view });
  };
  const actionUnavailable = !free || !map || !!readout?.tooFar;

  // R9: the picture of the saved view, drawn by a hidden map only while one is asked for.
  const Picture = useContext(PlanOpeningViewRendererContext);
  const onPictureFailed = useCallback(() => sayLook(PICTURE_FAILED_ANNOUNCEMENT), [sayLook]);
  // A move or turn saved after the picture was drawn is a new picture of the same view.
  const picture = useOpeningViewPicture(
    twinBid,
    storedView,
    onPictureFailed,
    `${baseline.latitude}|${baseline.longitude}|${baseline.orientation}`,
  );
  /*
   * The person's map comes first: the hidden map starts once the step's map has loaded, so the two
   * never load together (under software WebGL that kept the step's map from drawing in its bound),
   * and once the plan has been read, so a picture is never taken of the basemap alone while the plan
   * is on its way; or once that bound has passed, so neither leaves a picture waiting forever.
   */
  const [mapSeen, setMapSeen] = useState(false);
  useEffect(() => {
    if (map) setMapSeen(true);
  }, [map]);
  const [boundPassed, setBoundPassed] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setBoundPassed(true), PLAN_DRAW_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, []);
  const planSettled = planRead !== 'loading' && !(planRead === 'loaded' && planLayout === null);
  const pictureMayDraw = boundPassed || (mapSeen && planSettled);
  // The plan is drawn where the twin is saved, not where a live gesture shows it.
  const pictureTwin = useMemo(
    () =>
      ({
        ...twin,
        latitude: baseline.latitude,
        longitude: baseline.longitude,
        orientation: baseline.orientation,
      }) as AdminTwin,
    [twin, baseline.latitude, baseline.longitude, baseline.orientation],
  );
  const readoutId = useId();
  const actionHintId = useId();
  const viewTitleId = useId();

  /** The map is drawn: the renderer is provided and the plan has been read (or failed to be). */
  const mapReady = !!Canvas && planRead !== undefined;
  // R4.1: the map controls at the window's top left, then the map's overlay; both portalled to the
  // step's map host, so they sit over the whole window and come before the panel in the tab order.
  const mapPieces =
    mapReady && Canvas ? (
      <>
        <Card surface="on-scene" className="map-controls" role="group" aria-label="Map controls">
          {/* R4.1: only while the map is out of the plan view, once the map has drawn. */}
          {mapDrawn && free && (
            <Button
              className="camera-button"
              variant="frosted"
              size="sm"
              iconLeft={<Icon name="eye" />}
              onClick={() => setPlanViewRequest(n => n + 1)}
            >
              {SWITCH_BACK_LABEL}
            </Button>
          )}
          {free && readout && (
            <p id={readoutId} className="map-help readout">
              {readout.text}
              {readout.tooFar ? `. ${TOO_FAR}` : ''}
            </p>
          )}
          <p id={helpId} className="map-help">
            {PLACEMENT_HELP}
          </p>
          <p id={keyboardHelpId} style={visuallyHidden}>
            {KEYBOARD_HELP}
          </p>
          <div role="status" aria-live="polite" className="map-note">
            {note}
          </div>
          <div role="status" aria-live="polite" className="look-status" style={visuallyHidden}>
            {lookMessage}
          </div>
        </Card>
        <Canvas
          twin={twin}
          twinBid={twinBid}
          shown={shown}
          layout={planLayout}
          draggable={draggable}
          satellite={satellite}
          describedBy={`${helpId} ${keyboardHelpId}`}
          onPreview={onPreview}
          onCommit={onPlacementCommit}
          onSatelliteError={onSatelliteError}
          onMap={setMap}
          onFreeChange={setFree}
          planViewRequest={planViewRequest}
          // The readout is drawn only for a placed twin (it says the distance from it).
          freeDescribedBy={
            placedOrigin
              ? `${readoutId} ${helpId} ${keyboardHelpId}`
              : `${helpId} ${keyboardHelpId}`
          }
          onDrawnChange={setMapDrawn}
        />
      </>
    ) : null;

  return (
    <>
      {mapHost && mapPieces && createPortal(mapPieces, mapHost)}
      <Card surface="on-glass" inlay className="values" ref={valuesRef} tabIndex={-1}>
        <div className="fields">
          <div className="source-line">
            <span className="source-label">Position</span>
            {tag ? (
              <Tag surface="on-glass" size="dense" tone={tag.tone}>
                {tag.text}
              </Tag>
            ) : (
              !baseline.latitude.trim() && <span className="unset">Not on the twin record</span>
            )}
          </div>
          <PositionControls
            saved={baseline}
            coords={editor.coords}
            errors={{ latitude: editor.errors.latitude, longitude: editor.errors.longitude }}
            onCoordChange={editor.onCoordChange}
            onCoordCommit={editor.onCoordCommit}
            onCoordRestore={editor.onCoordRestore}
            geocode={geocode}
            onFind={() => void editor.onFind()}
            orientation={{
              value: editor.orientation,
              error: editor.errors.orientation,
              onChange: editor.onOrientationChange,
            }}
            open
            addressAction="find-or-reset"
            onReset={() => void editor.onReset()}
          />
        </div>
      </Card>
      {/* R6: under the Position card, as frame 11 orders them. */}
      <Card
        surface="on-glass"
        inlay
        className="opening-view"
        role="group"
        aria-labelledby={viewTitleId}
      >
        <div className="view-head">
          <Icon name="camera" size="sm" />
          <span id={viewTitleId} className="view-title">
            Opening view
          </span>
          <Tag surface="on-glass" size="dense" tone={storedView ? 'mint' : 'neutral'}>
            {storedView ? 'Saved' : 'Not saved'}
          </Tag>
        </div>
        <div className="picture" aria-busy={picture.shown.state === 'drawing' || undefined}>
          {!storedView ? (
            <p className="picture-empty">{hasStoredValue ? UNREADABLE_VIEW : NO_VIEW}</p>
          ) : picture.shown.state === 'drawn' ? (
            <img
              src={picture.shown.url}
              width={OPENING_VIEW_PICTURE_WIDTH_PX}
              height={OPENING_VIEW_PICTURE_HEIGHT_PX}
              alt={describeView(storedView)}
            />
          ) : picture.shown.state === 'failed' || !Picture ? (
            <p className="picture-empty">{PICTURE_FAILED}</p>
          ) : (
            <Skeleton shape="block" flush className="picture-skeleton" />
          )}
        </div>
        {Picture && picture.request && pictureMayDraw && (
          <Picture
            // A new hidden map per request: its camera is set only when it is built.
            key={picture.request.id}
            request={picture.request}
            twin={pictureTwin}
            twinBid={twinBid}
            layout={planLayout}
            onResult={picture.onResult}
          />
        )}
        {placedOrigin ? (
          <>
            <Button
              className="view-action"
              variant="primary"
              iconLeft={<Icon name="camera" />}
              aria-disabled={actionUnavailable || undefined}
              aria-describedby={!free ? actionHintId : readout?.tooFar ? readoutId : undefined}
              onClick={useThisView}
            >
              {hasStoredValue ? 'Replace' : 'Use this view'}
            </Button>
            {!free && (
              <p id={actionHintId} className="view-hint">
                {TILT_FIRST}
              </p>
            )}
          </>
        ) : (
          <p className="view-hint">{PLACE_FIRST}</p>
        )}
      </Card>
      {/* R3.1: the satellite check, last in the panel, as the map needs it. */}
      {mapReady && (
        <>
          <div className="satellite">
            <Switch
              surface="on-glass"
              checked={satelliteOn}
              onChange={evt => {
                setSatelliteOn(evt.target.checked);
                if (!evt.target.checked) setSatelliteFailed(false);
              }}
            >
              Satellite check
            </Switch>
            {satelliteOn && (
              <Slider
                className="opacity"
                surface="on-glass"
                size="sm"
                label="Satellite opacity"
                value={opacity}
                min={0}
                max={100}
                step={5}
                format={value => `${value}\u2009%`}
                onChange={setOpacity}
              />
            )}
          </div>
          <div role="status" aria-live="polite" className="satellite-status">
            {satelliteOn && satelliteFailed ? SATELLITE_FAILED : ''}
          </div>
        </>
      )}
    </>
  );
};

const OnboardingPositionOnMap: React.FC<Props> = ({
  className,
  twinBid,
  route,
  getAccessToken,
  save,
  planRead,
  planScope,
}) => {
  const [read, setRead] = useState<Read>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  /** A re-read once a save held at read time has landed (see `heldAtReadRef`). */
  const [reread, setReread] = useState(0);
  /** The save outcome last announced; its own region, so a read never masks it. */
  const [saveMessage, setSaveMessage] = useState('');
  const announceTimerRef = useRef<number | undefined>(undefined);
  /*
   * Criterion 13: each outcome is announced, the same one twice included. A polite region speaks
   * only when its text changes, so it is emptied first and refilled a moment later.
   */
  const announce = useCallback((message: string) => {
    setSaveMessage('');
    window.clearTimeout(announceTimerRef.current);
    announceTimerRef.current = window.setTimeout(() => setSaveMessage(message), 50);
  }, []);
  useEffect(() => () => window.clearTimeout(announceTimerRef.current), []);
  /*
   * A save that had failed and was held when the step read the twin is not in what it read (R7
   * reads anyway). Once *Retry saving* lands it, the step reads again, so its fields never send the
   * other half of a pair from before that save (review, Correctness).
   */
  const heldAtReadRef = useRef(false);
  /*
   * Where focus goes after *Try again* (review, Block A1). The failed card stays mounted while the
   * retry runs, so the focused button is never pulled out from under a keyboard user; a retry that
   * fails again leaves focus where it was, and one that succeeds moves it to the values card that
   * replaced the button.
   */
  const retriedRef = useRef(false);
  const valuesRef = useRef<HTMLDivElement>(null);
  // Read through a ref: a new function identity from the queue must not re-run the read.
  const whenSettledRef = useRef(save.whenSettled);
  whenSettledRef.current = save.whenSettled;
  const noteRecordRef = useRef(save.noteRecord);
  noteRecordRef.current = save.noteRecord;
  const readMarkRef = useRef(save.readMark);
  readMarkRef.current = save.readMark;
  /**
   * The read in flight is the re-read after a clash (`onboarding-multiplayer-twin-record-checks`,
   * R3.3): a failure leaves the step showing what it showed, rather than the read-failed screen.
   */
  const quietRef = useRef(false);
  const writtenSinceRef = useRef(save.writtenSince);
  writtenSinceRef.current = save.writtenSince;
  /** The twin on screen, once read: a quiet re-read changes only its clashed fields. */
  const shownTwinRef = useRef<AdminTwin | null>(null);
  shownTwinRef.current = read.state === 'ready' && read.bid === twinBid ? read.twin : null;
  /** The fields of this twin's clash: every one no read has brought back yet. */
  const stepClashFieldsRef = useRef<string[]>([]);
  stepClashFieldsRef.current = (save.clash?.bid === twinBid ? save.clash?.fields : null) ?? [];

  useEffect(() => {
    let cancelled = false;
    const startedAt = nextTick();
    void (async () => {
      let twin: AdminTwin | null = null;
      let mark: number | undefined;
      if (twinBid) {
        try {
          // R7: a save queued just before arriving lands first, so it is never shown as its old
          // value. A failed save still reads; the title bar keeps *Retry saving*.
          const settled = await whenSettledRef.current();
          if (cancelled) return;
          heldAtReadRef.current = !settled;
          const token = await getTokenBounded(getAccessToken);
          if (cancelled) return;
          mark = readMarkRef.current?.();
          twin = await getTwinById(Config.middlewareApi, token, twinBid, {
            timeout: TWIN_REQUEST_TIMEOUT_MS,
          });
        } catch {
          twin = null;
        }
      }
      if (cancelled) return;
      // Quiet only over a twin already on screen: a clash during the first read keeps that read's
      // failure screen (Copilot review).
      const shown = shownTwinRef.current;
      const quiet = quietRef.current && shown !== null;
      quietRef.current = false;
      if (twin && twinBid && quiet && shown) {
        // After a clash only the clashed fields change, on screen and as the next save's base, and
        // not one saved again while this read was in flight (R3.1, R3.3; Copilot review).
        const written = new Set(mark === undefined ? [] : writtenSinceRef.current(twinBid, mark));
        const theirs = Object.fromEntries(
          Object.entries(twin).filter(
            ([key]) => stepClashFieldsRef.current.includes(key) && !written.has(key),
          ),
        ) as Partial<AdminTwin>;
        noteRecordRef.current?.(twinBid, theirs, mark);
        setRead({ state: 'ready', twin: { ...shown, ...theirs }, bid: twinBid, startedAt });
      } else if (twin && twinBid) {
        // What the next save of each field says it saw (R3.1).
        noteRecordRef.current?.(twinBid, twin, mark);
        setRead({ state: 'ready', twin, bid: twinBid, startedAt });
      } else if (quiet) {
        console.error('[onboarding] position step could not re-read the twin', { twinBid });
      } else {
        console.error('[onboarding] position step could not read the twin', { twinBid });
        setRead({ state: 'failed', retrying: false });
      }
    })();
    return () => {
      cancelled = true;
    };
    // `getAccessToken` is Auth0's, stable for the session; re-reading on its identity would loop.
    // The page remounts this step per twin (`key={twinBid}`), so `twinBid` here is belt and braces.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twinBid, attempt, reread]);

  const status = save.status;
  useEffect(() => {
    if (status !== 'saved' || !heldAtReadRef.current) return;
    heldAtReadRef.current = false;
    setReread(n => n + 1);
  }, [status]);

  // A save of this twin was refused because someone else changed a field: show theirs (R3.3). Only a
  // clash that happens while the step is on screen: one standing from before it mounted is in the
  // step's own first read, which must stay a loud one (a quiet first read that failed would never
  // leave the loading state; review).
  const clashSeq = save.clash?.bid === twinBid ? save.clash?.seq : undefined;
  const seenClashSeqRef = useRef(clashSeq);
  useEffect(() => {
    if (clashSeq === undefined || clashSeq === seenClashSeqRef.current) return;
    seenClashSeqRef.current = clashSeq;
    quietRef.current = true;
    setReread(n => n + 1);
  }, [clashSeq]);

  useEffect(() => {
    if (read.state === 'ready' && retriedRef.current) {
      retriedRef.current = false;
      valuesRef.current?.focus();
    }
  }, [read.state]);

  const retrying = read.state === 'failed' && read.retrying;
  const retry = useCallback(() => {
    if (read.state !== 'failed' || retrying) return;
    retriedRef.current = true;
    setRead({ state: 'failed', retrying: true });
    setAttempt(a => a + 1);
  }, [read.state, retrying]);

  const announcement =
    read.state === 'ready'
      ? 'Position, orientation and opening view loaded'
      : read.state === 'failed' && !retrying
        ? 'Could not read the twin’s position'
        : '';

  const ready = read.state === 'ready' && read.bid === twinBid ? read : null;

  /*
   * While the read is in flight every row reads *Checking…*. Each row is ONE term and ONE description
   * (the dl content model), the decorative glyph inside the term. Once ready, position and
   * orientation are fields and the opening view is its own card.
   */
  const checkingRows = (
    <dl aria-busy>
      {POSITION_ROWS.map(({ key, label }) => (
        <div className="row" key={key}>
          <dt>
            <Icon name={ROW_ICON[key]} size="sm" />
            <span>{label}</span>
          </dt>
          <dd className="checking">
            <span className="value">Checking…</span>
          </dd>
        </div>
      ))}
    </dl>
  );

  /*
   * The panel (`onboarding-persistent-scene-position-twin`, R3): one floating docked card at the top
   * right, below the nav bar, moved by its grip and kept where this person left it, under a key of
   * its own (`positionTwin`), never Floor Plan's and Camera Feeds' `planCard`. Its travel starts at
   * the glass's start, less the card's edge, so it never slides under the step list (as Camera Feeds,
   * `camera-feeds-card-clear-of-list`), and never so far right that the panel no longer fits.
   */
  const { user } = useAuth0();
  const panelLeft = `min(calc(${GLASS_START} - ${GLASS_EDGE}), calc(100% - ${DOCKED_CARD_WIDTHS.beside} - 2 * ${GLASS_EDGE}))`;
  const cards = usePlanCardLayer(user?.sub, announce, PANEL_ROOM, panelLeft);
  const [mapHost, setMapHost] = useState<HTMLDivElement | null>(null);

  return (
    <div className={className}>
      {/* Mounted before the read starts, so the change is announced. */}
      <div role="status" aria-live="polite" style={visuallyHidden}>
        {announcement}
      </div>
      <div role="status" aria-live="polite" className="save-announcement" style={visuallyHidden}>
        {saveMessage}
      </div>
      {/* The map's controls and overlay (R4), first in the tab order. */}
      <div className="map-host" ref={setMapHost} />
      {cards.render(
        <PlanCard
          frame={cards.frame}
          id="positionTwin"
          name={PANEL_TITLE}
          homeInset={PANEL_HOME_INSET}
        >
          <DockedCard
            className="panel"
            placement="beside"
            icon={<Icon name="location-dot" />}
            title={PANEL_TITLE}
            headingLevel={4}
          >
            <p className="sentence">{positionSentence(route)}</p>
            {read.state === 'failed' ? (
              <Card surface="on-glass" inlay className="values failed">
                <p className="failed-line">Could not read the twin’s position.</p>
                <Button
                  variant="frosted"
                  size="sm"
                  aria-disabled={retrying || undefined}
                  onClick={retry}
                >
                  Try again
                </Button>
              </Card>
            ) : ready ? (
              <PositionFields
                key={ready.bid}
                twin={ready.twin}
                readStartedAt={ready.startedAt}
                twinBid={ready.bid}
                save={save}
                onAnnounce={announce}
                planRead={planRead}
                planScope={planScope}
                valuesRef={valuesRef}
                mapHost={mapHost}
              />
            ) : (
              <Card surface="on-glass" inlay className="values" ref={valuesRef} tabIndex={-1}>
                {checkingRows}
              </Card>
            )}
            <p className="never-blocks">This step never blocks Launch Twin</p>
          </DockedCard>
        </PlanCard>,
      )}
    </div>
  );
};

/** The panel's home: below the nav bar, at the 8 px edge (R3.2), as Camera Feeds' card. */
const PANEL_HOME_INSET = {
  top: PANEL_TOP_PX,
  right: GLASS_EDGE_PX,
  bottom: GLASS_EDGE_PX,
  left: GLASS_EDGE_PX,
};
/** The room the card layer leaves a tall panel, which scrolls inside itself (R3.3). */
const PANEL_ROOM = { top: PANEL_TOP_PX, bottom: GLASS_EDGE_PX };

export default styled(OnboardingPositionOnMap)`
  /* The whole window (R2.2): the map is the page backdrop's, under this; the content passes the
     pointer through, and the panel, the map controls and the handles take it back (R2.5). */
  position: absolute;
  inset: 0;

  .map-host {
    position: absolute;
    inset: 0;
  }

  /* R4.1: the map controls, at the window's top left beside the step list. */
  .map-controls {
    position: absolute;
    top: ${PANEL_TOP};
    left: ${GLASS_START};
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${space[2]};
    /* Layout geometry, not a brand value: the help line's measure. */
    max-width: 360px;
    padding: ${space[3]};
    pointer-events: auto;
  }

  .panel .sentence {
    margin: 0;
    ${typeRole.body1};
    color: ${semantic.text.secondary};
  }

  .values {
    width: 100%;
    text-align: left;
  }

  /* Out of the flow while silent, never out of the accessibility tree: a live region that appears
     with its text is not announced. */
  .map-note:empty,
  .satellite-status:empty {
    position: absolute;
  }

  .map-note,
  .satellite-status,
  .map-help {
    margin: 0;
    ${typeRole.caption1};
    color: ${semantic.text.secondary};
  }

  .map-note {
    color: ${semantic.text.primary};
  }

  .satellite {
    display: flex;
    align-items: center;
    gap: ${space[4]};
  }

  .satellite .opacity {
    flex: 1;
  }

  .fields {
    display: flex;
    flex-direction: column;
    gap: ${space[3]};
    padding: ${space[3]} ${space[2]};
  }

  .source-line {
    display: flex;
    align-items: center;
    gap: ${space[3]};
    ${typeRole.caption1};
    color: ${semantic.text.secondary};
  }

  .source-line .unset {
    color: ${semantic.text.placeholder};
  }

  dl {
    margin: 0;
  }

  .fields + dl .row {
    border-top: 1px solid ${semantic.border.faint};
  }

  .row {
    display: grid;
    /* Layout geometry, not a brand value: the icon-and-label column, then the value and its tag. */
    grid-template-columns: 9rem 1fr;
    align-items: center;
    gap: ${space[3]};
    padding: ${space[3]} ${space[2]};
  }

  .row + .row {
    border-top: 1px solid ${semantic.border.faint};
  }

  dt {
    display: flex;
    align-items: center;
    gap: ${space[3]};
    ${typeRole.caption1};
    color: ${semantic.text.secondary};
  }

  dd {
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${space[3]};
  }

  dd .value {
    font-family: ${fontMono};
    color: ${semantic.text.primary};
  }

  dd.checking .value {
    color: ${semantic.text.placeholder};
  }

  /* R6: the Opening view card. */
  .opening-view {
    display: flex;
    flex-direction: column;
    gap: ${space[3]};
    width: 100%;
    padding: ${space[3]} ${space[2]};
    text-align: left;
  }

  .view-head {
    display: flex;
    align-items: center;
    gap: ${space[3]};
    ${typeRole.caption1};
    color: ${semantic.text.secondary};
  }

  .view-title {
    flex: 1;
  }

  /* Layout geometry, not a brand value: the picture's size, as frame 11 draws the thumbnail. */
  .picture {
    position: relative;
    width: 320px;
    max-width: 100%;
    height: 180px;
    align-self: center;
    overflow: hidden;
    border: 1px solid ${semantic.border.faint};
    border-radius: ${radius.md};
  }

  .picture-skeleton {
    width: 100%;
    height: 100%;
  }

  .picture img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .picture-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    margin: 0;
    padding: ${space[3]};
    text-align: center;
    ${typeRole.caption1};
    color: ${semantic.text.secondary};
  }

  .view-action {
    width: 100%;
  }

  .view-hint {
    margin: 0;
    ${typeRole.caption1};
    color: ${semantic.text.secondary};
  }

  .values:focus-visible {
    outline: 2px solid ${semantic.interactive.focusRing};
    outline-offset: 2px;
  }

  .failed {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${space[3]};
  }

  .failed-line {
    margin: 0;
    color: ${semantic.text.primary};
  }

  .never-blocks {
    margin: 0;
    ${typeRole.caption1};
    color: ${semantic.text.secondary};
  }
`;
